import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { removeToken } from './utils/token';
import { LME_OMS_API } from './services/typings';
import { addTokenToHeaderInterceptor, blockUserActionResponseInterceptor, requestErrorHandler } from './utils/request';
import { queryCurrentUser } from './services/api';
import EnvironmentReminder from './components/RightContent/EnvironmentReminder';

// const isDev = process.env.NODE_ENV === 'development';

const PublicRoute: string[] = [
  '/login',
  '/privacy-policy',
  '/account/recovery',
  '/account/invitation',
  '/account/invitation/accept',
  '/account/invitation/result',
];


/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: LME_OMS_API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<LME_OMS_API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const response = await queryCurrentUser();
      return response.data;
    } catch (error) {
      removeToken();
      // history.push('/login');
    }
    return undefined;
  };
  // 如果是登录页面，不执行
  const { location } = history;

  if (PublicRoute.includes(location.pathname)) {
    console.log('return', location.pathname);
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  // if (location.pathname === loginPath) {
  //   // 如果是登录页面，不执行
  //   return {
  //     fetchUserInfo,
  //     settings: defaultSettings as Partial<LayoutSettings>,
  //   };
  // }
  // 如果不是登录页面，执行
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<EnvironmentReminder />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: async () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !PublicRoute.includes(location.pathname)) {
        // console.log('initialState', initialState);
        // console.log('history.location?.pathname', history.location?.pathname);
        // console.log(
        //   'logout SMS .onPageChange',
        //   initialState,
        //   initialState?.currentUser,
        //   history.location?.pathname,
        // );

        const userInfo = await initialState?.fetchUserInfo?.();
        if (userInfo) {
          setInitialState((s) => ({
            ...s,
            currentUser: userInfo,
          }));
        } else {
          history.push('/login');
        }
        // history.push('/login');
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links:[],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
        </>
      );
    },
    ...initialState?.settings,

      contentWidth: 'Fluid',
      siderMenuType: 'group',
      layout: 'top',
      fixedHeader:true,
      fixSiderbar:false,
      colorWeak:false,
      colorPrimary: '#00264c',
      token: {
        sider: {
          colorBgMenuItemHover: 'rgba(0,0,0,0.1)',
          colorBgMenuItemActive: 'rgba(133,184,255,0.5)',
          colorBgMenuItemSelected: 'rgba(133,184,255,0.5)',
        },
        header: {
          colorBgHeader: '#162843',
          colorHeaderTitle: '#fff',
          // colorBgScrollHeader: 'red',
          colorTextMenu: '#fff',
          colorTextMenuSecondary: '#dfdfdf',
          colorTextMenuSelected: '#fff',
          colorBgMenuItemHover: '#283446',
          colorBgMenuItemSelected: '#1d4b91',
          colorTextMenuActive: 'rgba(255,255,255,0.85)',
          colorTextRightActionsItem: '#fff',
          colorBgRightActionsItemHover: '#1890ff',
        },
        pageContainer: {
          paddingBlockPageContainerContent: 0,
        },
        colorTextAppListIconHover: '#fff',
        colorTextAppListIcon: '#dfdfdf',
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  credentials: 'include', // 默认请求是否带上cookie
  timeout: 600000,
  headers: {
    'Accept-Language': 'en-US',
    Accept: 'application/json',
  },
  requestInterceptors: [addTokenToHeaderInterceptor],
  responseInterceptors: [blockUserActionResponseInterceptor],

  errorConfig: {
    errorHandler: requestErrorHandler, // 默认错误处理
  },
};
